/**
 * MaintenanceInFlightPanel — the work a customer already has in flight, shown on the
 * maintenance notice, with the exit that actually works for each one (OMEGA-1024 FR21).
 *
 * It exists because the Console and the API would otherwise disagree about the same
 * request. Maintenance stops NEW workload only: cancel, abort and every read stay open
 * on the server, so a full-screen notice that hid them would leave a customer who wants
 * out of a stop with no way to say so from the product.
 *
 * THE EXIT IS PER STATUS, AND THAT IS THE POINT OF THE COMPONENT, not a detail of it.
 * A job past routing is cancelled with `POST /jobs/{id}:cancel`. A `waiting_for_inputs`
 * job is NOT: `cancel_job` has no branch for that status, and a parked job's
 * `provider_run_id` is NULL, so it falls through to `409 not_cancellable` every time.
 * Its exit is `POST /jobs/{id}/inputs:abort`, which needs the session id, which is why
 * each parked job costs one extra read of `GET /jobs/{id}/inputs`. One Cancel button for
 * both statuses would look correct and never work, while the parked job went on holding
 * a concurrency slot (2 on the free plan).
 *
 * What it deliberately does NOT offer: any way to submit, any upload control, and any
 * link into the Console shell. The notice pre-empts the shell; a panel that led back
 * into it would undo that.
 *
 * It degrades to NOTHING — no empty state, no error box — when the read fails or comes
 * back empty. The panel is an affordance, not a status report, so a failed `GET /jobs`
 * must leave the maintenance message standing rather than replace it with an error
 * screen. The caller renders it only for a session that already holds a full token, so
 * a logged-out visitor never reaches this file at all.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../engine/api";
import usePolledFetch from "../hooks/usePolledFetch";
import CancelJobButton from "./CancelJobButton";
import { statusLabel, fmtTime } from "./jobDisplay";
import { isTerminal } from "../engine/jobResults";

// The one status whose exit is abort rather than cancel, and whose files are held
// rather than running.
const PARKED_STATUS = "waiting_for_inputs";

// A stop is minutes-to-hours, not seconds, and every row here is already waiting on
// something. 30s keeps a cancelled job from lingering without polling for its own sake.
const POLL_MS = 30000;

// Enough to cover any plan's concurrency quota (2 / 10 / 100) without paging: this is a
// list of what is in flight, and in flight is bounded by the quota.
const JOB_LIMIT = 100;

/**
 * Cancel a `waiting_for_inputs` job through `inputs:abort` — the exit that exists for a
 * parked job (FR21).
 *
 * Two-step confirm, matching CancelJobButton: the act is irreversible (the files already
 * uploaded are discarded and the job is cancelled), so it is never one click.
 *
 * The double-submit guard reads a REF, not the `busy` state, and the difference is the whole
 * guard. React batches state updates, so two clicks dispatched in one batch both read the
 * `busy` their render closed over — `false` for both — and `disabled` has not repainted yet
 * either. A state-based check therefore admits the second click and fires two aborts. The ref
 * is written synchronously, before the first `await`, so the second click sees it. `busy`
 * stays as state because it drives what the button paints; the ref decides whether the
 * request happens. A guard placed after the request is not a guard, and a guard that cannot
 * see its own first entry is not one either.
 */
function AbortUploadButton({ jobId, sessionId, onAborted }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState("");
  const busyRef = useRef(false);

  async function doAbort() {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      await api.abortInputs(jobId, sessionId);
      // The 200 is the authority. An unconfirmed response is not proof of cancellation,
      // which is why nothing is claimed in the catch below.
      setConfirming(false);
      setReceipt("Upload cancelled.");
      if (typeof onAborted === "function") onAborted();
    } catch (err) {
      setError(err.message || "We could not cancel this upload. Try again.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  if (receipt) {
    return <div className="text-sm text-fg2">{receipt}</div>;
  }

  if (!confirming) {
    return (
      <div className="space-y-1">
        <button
          type="button"
          onClick={() => setConfirming(true)}
          data-testid={`maintenance-abort-${jobId}`}
          className="text-sm text-danger hover:text-danger cursor-pointer bg-transparent border border-danger/40 rounded-none px-3 py-1"
        >
          Cancel upload
        </button>
        {error && <div className="text-sm text-danger bg-danger-tint rounded-none px-3 py-2">{error}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-sm text-fg">
        Cancel this upload? The files you have already sent are discarded and this workload is cancelled.
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={doAbort}
          disabled={busy}
          data-testid={`maintenance-abort-confirm-${jobId}`}
          className="text-sm text-danger cursor-pointer bg-danger-tint rounded-none px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? "Cancelling..." : "Yes, cancel upload"}
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          disabled={busy}
          className="text-sm text-fg2 hover:text-fg cursor-pointer bg-transparent border border-line rounded-none px-3 py-1 disabled:opacity-50"
        >
          Keep it
        </button>
      </div>
      {error && <div className="text-sm text-danger bg-danger-tint rounded-none px-3 py-2">{error}</div>}
    </div>
  );
}

/**
 * The held-until-maintenance-ends line for a parked job.
 *
 * It states the job's OWN expiry and never promises the files will still be there, and
 * the distinction is three orders of magnitude wide: `expires_at` is fixed when the
 * upload session is created, and the janitor expires a session on that instant whether
 * or not every byte already arrived. So a session opened 23h59m ago is lost to a
 * ten-minute stop. "We will hold it until maintenance ends" would be false exactly when
 * it mattered most.
 *
 * With no expiry to state (the session read failed) it says so rather than filling the
 * gap with the comfortable reading. While that read is still in flight it says NOTHING
 * about the expiry: "we could not read it" is a claim about a settled outcome, and
 * printing it over a request that has not come back yet is false for as long as the
 * request takes.
 */
function HeldNotice({ expiresAt, pending }) {
  return (
    <div className="text-sm text-fg2 leading-relaxed">
      <div>Held until maintenance ends. Your files stay where they are and nothing is submitted meanwhile.</div>
      {expiresAt ? (
        <div>
          This upload expires on {fmtTime(expiresAt)}. If maintenance is still on then, the workload ends and you
          can start it again afterwards.
        </div>
      ) : pending ? null : (
        <div>We could not read when this upload expires.</div>
      )}
    </div>
  );
}

export default function MaintenanceInFlightPanel() {
  // `status_group=pending` is the server-side complement of the terminal statuses, so
  // the filtering is done where the data is rather than over a page of mixed rows.
  const readJobs = useCallback(() => api.listJobs(JOB_LIMIT, undefined, "pending"), []);
  const { data, loading, refresh } = usePolledFetch(readJobs, { intervalMs: POLL_MS });

  // Belt and braces over the server filter: a row that terminalised between the query
  // and the render has no exit left to offer, and offering one would be a control that
  // answers 409.
  const jobs = (data?.jobs || []).filter((job) => job && job.job_id && !isTerminal(job.status));
  const parkedIds = jobs
    .filter((job) => job.status === PARKED_STATUS)
    .map((job) => job.job_id)
    .join(",");

  // job_id -> { sessionId, expiresAt }. Resolved one read at a time, only for parked
  // jobs, and only once each: `GET /jobs` cannot carry the session id, and abort cannot
  // be offered without one.
  const [sessions, setSessions] = useState({});
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;

  useEffect(() => {
    if (!parkedIds) return undefined;
    let cancelled = false;
    for (const jobId of parkedIds.split(",")) {
      if (sessionsRef.current[jobId]) continue;
      // Marked in flight before the call so a re-render mid-read does not issue a second.
      sessionsRef.current = { ...sessionsRef.current, [jobId]: { pending: true } };
      setSessions((prev) => ({ ...prev, [jobId]: { pending: true } }));
      api
        .getJobInputs(jobId)
        .then((resp) => {
          if (cancelled) return;
          setSessions((prev) => ({
            ...prev,
            [jobId]: {
              sessionId: resp?.upload_session?.session_id || "",
              expiresAt: resp?.upload_session?.expires_at || null,
            },
          }));
        })
        .catch(() => {
          if (cancelled) return;
          // No session id means no abort control. A button that cannot reach the
          // endpoint is worse than none, and the held line says what is unknown.
          setSessions((prev) => ({ ...prev, [jobId]: { sessionId: "", expiresAt: null } }));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [parkedIds]);

  // FR21 — degrade to the plain notice. An error is NOT rendered: the maintenance
  // message is the thing the customer came for, and a read that failed has nothing to
  // add to it. The same for an empty list, which is the common case.
  //
  // The test is "are there jobs to show", NOT "did the last read fail", and the
  // difference shows up mid-session: usePolledFetch keeps the last good payload when a
  // later tick fails, so keying the empty branch off `error` would blank a panel the
  // customer was already reading, and could blank it the moment after they cancelled
  // something. `error` with no payload lands here anyway, because `jobs` is then empty.
  if (loading) {
    return (
      <div role="status" data-testid="maintenance-inflight-loading" className="text-sm text-fg2">
        Checking for work you already have running.
      </div>
    );
  }
  if (jobs.length === 0) return null;

  return (
    <section
      aria-labelledby="maintenance-inflight-title"
      data-testid="maintenance-inflight"
      className="border-t border-line pt-4"
    >
      <h2 id="maintenance-inflight-title" className="text-sm font-medium text-fg mb-1">
        Work you already have with us
      </h2>
      <p className="text-sm text-fg2 mb-3 leading-relaxed">
        Maintenance does not touch work we have already taken on. These keep going, and you can stop any of them
        from here.
      </p>
      <ul className="space-y-3">
        {jobs.map((job) => {
          const parked = job.status === PARKED_STATUS;
          const session = sessions[job.job_id];
          return (
            <li
              key={job.job_id}
              data-testid={`maintenance-job-${job.job_id}`}
              className="border border-line rounded-none px-3 py-2 bg-surface2"
            >
              <div className="flex justify-between gap-3 items-baseline">
                <span className="text-sm text-fg break-words">{job.workload_name || job.job_id}</span>
                <span className="text-sm text-fg2 text-right">{statusLabel(job.status)}</span>
              </div>
              {parked && (
                <div className="mt-1">
                  <HeldNotice expiresAt={session?.expiresAt} pending={!session || session.pending} />
                </div>
              )}
              <div className="mt-2">
                {parked ? (
                  session?.sessionId ? (
                    <AbortUploadButton jobId={job.job_id} sessionId={session.sessionId} onAborted={refresh} />
                  ) : null
                ) : (
                  <CancelJobButton jobId={job.job_id} status={job.status} onCancelled={refresh} />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
